import React from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Play, Trash2, Check, Mic } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

const fileUrlWithAuth = (filename) => {
  const t = localStorage.getItem("gl26_auth_token");
  const base = `${API}/files/recording/${filename}`;
  return t ? `${base}?t=${encodeURIComponent(t)}` : base;
};

export const RecordingsDialog = ({ open, onOpenChange, song, onUpdated }) => {
  const recordings = React.useMemo(() => {
    const list = [...(song?.recordings || [])];
    list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [song]);

  const activeFilename = song?.active_recording_filename;
  const [playingId, setPlayingId] = React.useState(null);
  const audioRef = React.useRef(null);

  const playOne = (filename) => {
    if (playingId === filename) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (!audioRef.current) audioRef.current = new Audio();
    audioRef.current.src = fileUrlWithAuth(filename);
    audioRef.current.play().then(() => setPlayingId(filename)).catch(() => setPlayingId(null));
    audioRef.current.onended = () => setPlayingId(null);
  };

  React.useEffect(() => () => { audioRef.current?.pause(); audioRef.current = null; }, []);

  const activate = async (filename) => {
    try {
      const { data } = await axios.post(`${API}/songs/${song.id}/recordings/${filename}/activate`);
      onUpdated?.(data);
      toast.success("Enregistrement actif");
    } catch (e) {
      toast.error("Activation impossible");
    }
  };

  const del = async (filename) => {
    if (!window.confirm("Supprimer cet enregistrement ?")) return;
    try {
      const { data } = await axios.delete(`${API}/songs/${song.id}/recordings/${filename}`);
      onUpdated?.(data);
      toast.success("Supprimé");
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  const rename = async (filename, currentLabel) => {
    const name = window.prompt("Nom de l'enregistrement (optionnel) :", currentLabel || "");
    if (name === null) return;
    try {
      const { data } = await axios.patch(`${API}/songs/${song.id}/recordings/${filename}`, { label: name.trim() });
      onUpdated?.(data);
    } catch (e) {
      toast.error("Renommage impossible");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0d0d0d] border-white/10 text-white max-w-md max-h-[85vh] overflow-y-auto no-scrollbar"
        data-testid="recordings-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg text-[var(--gl-gold)] font-display flex items-center gap-2">
            <Mic size={18} /> Enregistrements · {song?.id}
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-xs">
            Tape un enregistrement pour l'écouter, ★ pour le rendre actif sur le bouton LIVE.
          </DialogDescription>
        </DialogHeader>

        {recordings.length === 0 ? (
          <div className="text-center text-neutral-500 text-sm py-8 border border-dashed border-white/10 rounded-xl">
            Aucun enregistrement pour ce morceau.
            <br />
            Tape le bouton <span className="text-red-400 font-bold">LIVE</span> pour démarrer.
          </div>
        ) : (
          <ul className="space-y-2">
            {recordings.map((r, idx) => {
              const isActive = r.filename === activeFilename;
              const isPlaying = playingId === r.filename;
              return (
                <li
                  key={r.filename}
                  className={`rounded-xl border bg-black p-2.5 flex items-center gap-2 ${
                    isActive ? "border-[var(--gl-gold)]" : "border-white/10"
                  }`}
                  data-testid={`recording-item-${r.filename}`}
                >
                  <button
                    onClick={() => playOne(r.filename)}
                    className="w-10 h-10 rounded-full bg-[var(--gl-gold)] text-black flex items-center justify-center hover:brightness-110 shrink-0"
                    data-testid={`recording-play-${r.filename}`}
                    aria-label={isPlaying ? "Pause" : "Lire"}
                  >
                    {isPlaying ? <span className="block w-3 h-3 bg-black" /> : <Play size={16} className="ml-0.5" />}
                  </button>
                  <button
                    onClick={() => rename(r.filename, r.label)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="text-sm font-bold text-white truncate">
                      {r.label || `Live #${recordings.length - idx}`}
                    </div>
                    <div className="text-[10px] text-neutral-400 tabular-nums">
                      {fmtDate(r.created_at)}
                    </div>
                  </button>
                  <button
                    onClick={() => activate(r.filename)}
                    disabled={isActive}
                    className={`w-8 h-8 rounded-full flex items-center justify-center border shrink-0 ${
                      isActive
                        ? "bg-[var(--gl-gold)]/20 border-[var(--gl-gold)] text-[var(--gl-gold)]"
                        : "bg-[#161616] border-white/10 hover:border-[var(--gl-gold)]/60"
                    }`}
                    data-testid={`recording-activate-${r.filename}`}
                    aria-label="Rendre actif"
                    title={isActive ? "Actif" : "Activer"}
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={() => del(r.filename)}
                    className="w-8 h-8 rounded-full flex items-center justify-center bg-[#161616] border border-white/10 hover:border-orange-400 hover:text-orange-400 shrink-0"
                    data-testid={`recording-delete-${r.filename}`}
                    aria-label="Supprimer"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecordingsDialog;
